"use client";
import React, { useState, useRef, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { RiInformationLine } from "react-icons/ri";
import { FiCheckCircle } from "react-icons/fi";
import { FiXCircle } from "react-icons/fi";
import { LuBookOpenText } from "react-icons/lu";
import { useGlobalStore } from "~/stores/store-provider";

export type ShopifyConnectionData = {
  connection_name: string;
  shop_name: string;
  webhook_version: string;
  access_token: string;
  type: string;
};

type ShopifyConnectorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isConnecting?: boolean;
  initialData?: Partial<ShopifyConnectionData>;
};

const ShopifyConnector: React.FC<ShopifyConnectorProps> = ({
  open,
  onOpenChange,
}) => {
  const modalRef = useRef<HTMLDialogElement>(null);
  const [formData, setFormData] = useState<ShopifyConnectionData>({
    connection_name: "",
    shop_name: "",
    webhook_version: "2025-07",
    access_token: "",
    type: "shopify",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isCalloutExiting, setIsCalloutExiting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { addConnection } = useGlobalStore((state) => state);

  const handleInputChange = (
    field: keyof ShopifyConnectionData,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current?.showModal();
    } else if (!open && modalRef.current) {
      modalRef.current?.close();
    }
  }, [open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        connection_name: "",
        shop_name: "",
        webhook_version: "2025-07",
        access_token: "",
        type: "shopify",
      });
      setIsLoading(false);
      setIsConnected(null);
      setIsCalloutExiting(false);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await addConnection(formData);

      // Clear form data after successful connection
      setFormData({
        connection_name: "",
        shop_name: "",
        webhook_version: "2025-07",
        access_token: "",
        type: "shopify",
      });

      setIsConnected(true);
      setIsCalloutExiting(false);
      setTimeout(() => {
        setIsCalloutExiting(true);
        setTimeout(() => {
          setIsConnected(null);
          setIsCalloutExiting(false);
        }, 300);
      }, 2700);

      setTimeout(() => {
        onOpenChange(false);
      }, 2000);
    } catch (error: any) {
      setIsLoading(false);
      setIsConnected(false);
      setIsCalloutExiting(false);
      setErrorMessage(error?.response?.data?.detail);

      setTimeout(() => {
        setIsCalloutExiting(true);
        setTimeout(() => {
          setIsConnected(null);
          setIsCalloutExiting(false);
        }, 300);
      }, 4000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const isFormValid = () => {
    return (
      formData?.connection_name.trim() &&
      formData?.shop_name.trim() &&
      formData?.webhook_version.trim() &&
      formData?.access_token.trim()
    );
  };

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box max-w-6xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-lg">Connect to Shopify</h3>
          <button
            className="btn btn-ghost btn-circle btn-sm"
            onClick={handleCancel}
          >
            <IoClose size={20} />
          </button>
        </div>

        <div className="hero bg-base-200 rounded-lg">
          <div className="hero-content p-6 flex-col lg:flex-row-reverse w-full">
            <div className="flex-1">
              <h2 className="text-2xl font-bold">
                Find Your Store Credentials
              </h2>
              <p className="py-4">
                Locate your existing Shopify store connection details:
              </p>
              <ul className="steps steps-vertical">
                <li className="step step-info">
                  <div className="text-left">
                    <div className="font-semibold">Store Domain</div>
                    <div className="text-sm text-gray-500 p-2">
                      Use your store's URL (e.g., yourstore.myshopify.com)
                    </div>
                  </div>
                </li>
                <li className="step step-info">
                  <div className="text-left">
                    <div className="font-semibold">Access App Development</div>
                    <div className="text-sm text-gray-500 p-2">
                      Go to Settings → Apps and sales channels → Click "Develop
                      apps"
                    </div>
                  </div>
                </li>
                <li className="step step-info">
                  <div className="text-left">
                    <div className="font-semibold">Find Your Custom Apps</div>
                    <div className="text-sm text-gray-500 p-2">
                      View existing custom apps (legacy private apps and
                      storefront apps) or create new ones
                    </div>
                  </div>
                </li>
                <li className="step step-info">
                  <div className="text-left">
                    <div className="font-semibold">Get Access Token</div>
                    <div className="text-sm text-gray-500 p-2">
                      Click on your app → Admin API access token section →
                      Reveal token once
                    </div>
                  </div>
                </li>
                <li className="step step-info">
                  <div className="text-left">
                    <div className="font-semibold">Webhook Version</div>
                    <div className="text-sm text-gray-500 p-2">
                      Use the latest webhook version (2025-07) or check your
                      app's configured version
                    </div>
                  </div>
                </li>
              </ul>
              <div className="mt-4">
                <a
                  href="https://help.shopify.com/en/manual/apps/app-types/custom-apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link link-primary text-sm"
                >
                  <LuBookOpenText size={20} className="inline mr-2" />
                  Official Shopify Custom Apps Documentation
                </a>
              </div>
            </div>
            <div className="divider divider-horizontal" />
            <div className="flex-1">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">
                      Connection Name
                    </span>
                    <span className="label-text-alt text-red-500">*</span>
                    <div
                      className="tooltip tooltip-right"
                      data-tip="A friendly name for this connection"
                    >
                      <RiInformationLine className="text-gray-500" />
                    </div>
                  </label>
                  <input
                    type="text"
                    placeholder="My Shopify Store"
                    className="input input-bordered w-full"
                    value={formData?.connection_name}
                    onChange={(e) =>
                      handleInputChange("connection_name", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Shop Name</span>
                    <span className="label-text-alt text-red-500">*</span>
                    <div
                      className="tooltip tooltip-right"
                      data-tip="Your store domain (e.g, my-store.myshopify.com)"
                    >
                      <RiInformationLine className="text-gray-500" />
                    </div>
                  </label>
                  <input
                    type="text"
                    placeholder="my-store.myshopify.com"
                    className="input input-bordered w-full"
                    value={formData?.shop_name}
                    onChange={(e) =>
                      handleInputChange("shop_name", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">
                      Webhook Version
                    </span>
                    <span className="label-text-alt text-red-500">*</span>
                    <div
                      className="tooltip tooltip-right"
                      data-tip="Shopify webhook version to use"
                    >
                      <RiInformationLine className="text-gray-500" />
                    </div>
                  </label>
                  <select
                    className="select select-bordered w-full"
                    value={formData?.webhook_version}
                    onChange={(e) =>
                      handleInputChange("webhook_version", e.target.value)
                    }
                    required
                  >
                    <option value="2025-07">2025-07</option>
                    <option value="2025-04">2025-04</option>
                    <option value="2025-01">2025-01</option>
                    <option value="2024-10">2024-10</option>
                    <option value="2024-07">2024-07</option>
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">Access Token</span>
                    <span className="label-text-alt text-red-500">*</span>
                    <div
                      className="tooltip tooltip-right"
                      data-tip="OAuth token or private app token for authentication"
                    >
                      <RiInformationLine className="text-gray-500" />
                    </div>
                  </label>
                  <input
                    type="password"
                    placeholder="shpat_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="input input-bordered w-full"
                    value={formData?.access_token}
                    onChange={(e) =>
                      handleInputChange("access_token", e.target.value)
                    }
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-neutral w-full mt-4 bg-green-600 text-white hover:bg-green-500"
                  disabled={!isFormValid()}
                >
                  {isLoading ? (
                    <span className="loading loading-spinner"></span>
                  ) : (
                    "Connect to Shopify"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      {isConnected !== null && (
        <div className="toast toast-top toast-center z-50">
          <div
            className={`alert ${
              isConnected ? "alert-success" : "alert-error"
            } ${
              isCalloutExiting
                ? "opacity-0 -translate-y-full"
                : "opacity-100 translate-y-0"
            } transition-all duration-300 ease-in-out`}
          >
            {isConnected ? (
              <FiCheckCircle className="h-6 w-6" />
            ) : (
              <FiXCircle className="h-6 w-6" />
            )}
            <span>{isConnected ? "Connection successful" : errorMessage}</span>
          </div>
        </div>
      )}
    </dialog>
  );
};

export default ShopifyConnector;
